import { useState, useCallback } from 'react';

export type SortOption =
  | 'date-desc' | 'date-asc'
  | 'title-asc' | 'title-desc'
  | 'size-asc' | 'size-desc'
  | 'pages-asc' | 'pages-desc'
  | 'chunks-asc' | 'chunks-desc'
  | 'shortName-asc' | 'shortName-desc'
  | 'shortCompany-asc' | 'shortCompany-desc'
  | 'category-asc' | 'category-desc'
  | 'processing-asc' | 'processing-desc'
  | 'company-asc' | 'company-desc'
  | 'docType-asc' | 'docType-desc'
  | 'period-asc' | 'period-desc'
  | 'code-asc' | 'code-desc'
  | 'status-asc' | 'status-desc';

export interface TableItem {
  id: string;
  type: 'document' | 'saved-message' | 'snippet' | 'studio-result';
  category: 'compartido' | 'privado' | 'snippet' | 'mensaje' | 'studio';
  name: string;
  date: string;
  dateValue: Date;
  size: string;
  sizeValue: number;
  pages: number;
  chunks: number;
  docType: string;
  company: string;
  status: string;
  origin: 'private' | 'shared';
  companyName: string;
  documentType: string;
  documentPeriod: string;
  documentCode: string;
  processingTimeSeconds: number | null;
  processingMethods: string[];
  data: any;
}

export const useDriveSorting = () => {
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');

  const sortItems = useCallback((items: TableItem[]): TableItem[] => {
    return [...items].sort((a, b) => {
      switch (sortBy) {
        case 'title-asc':
          return a.name.localeCompare(b.name);
        case 'title-desc':
          return b.name.localeCompare(a.name);
        case 'date-asc':
          return a.dateValue.getTime() - b.dateValue.getTime();
        case 'date-desc':
          return b.dateValue.getTime() - a.dateValue.getTime();
        case 'size-asc':
          return a.sizeValue - b.sizeValue;
        case 'size-desc':
          return b.sizeValue - a.sizeValue;
        case 'pages-asc':
          return a.pages - b.pages;
        case 'pages-desc':
          return b.pages - a.pages;
        case 'chunks-asc':
          return a.chunks - b.chunks;
        case 'chunks-desc':
          return b.chunks - a.chunks;
        case 'shortName-asc': {
          const aDoc = a.type === 'document' ? (a.data as any) : null;
          const bDoc = b.type === 'document' ? (b.data as any) : null;
          const aName = aDoc?.name || '';
          const bName = bDoc?.name || '';
          return aName.localeCompare(bName);
        }
        case 'shortName-desc': {
          const aDoc = a.type === 'document' ? (a.data as any) : null;
          const bDoc = b.type === 'document' ? (b.data as any) : null;
          const aName = aDoc?.name || '';
          const bName = bDoc?.name || '';
          return bName.localeCompare(aName);
        }
        case 'shortCompany-asc': {
          const aDoc = a.type === 'document' ? (a.data as any) : null;
          const bDoc = b.type === 'document' ? (b.data as any) : null;
          const aCompany = aDoc?.company_name || '';
          const bCompany = bDoc?.company_name || '';
          return aCompany.localeCompare(bCompany);
        }
        case 'shortCompany-desc': {
          const aDoc = a.type === 'document' ? (a.data as any) : null;
          const bDoc = b.type === 'document' ? (b.data as any) : null;
          const aCompany = aDoc?.company_name || '';
          const bCompany = bDoc?.company_name || '';
          return bCompany.localeCompare(aCompany);
        }
        case 'category-asc':
          return a.category.localeCompare(b.category);
        case 'category-desc':
          return b.category.localeCompare(a.category);
        case 'processing-asc': {
          const timeA = a.processingTimeSeconds ?? Number.POSITIVE_INFINITY;
          const timeB = b.processingTimeSeconds ?? Number.POSITIVE_INFINITY;
          return timeA - timeB;
        }
        case 'processing-desc': {
          const timeA = a.processingTimeSeconds ?? Number.NEGATIVE_INFINITY;
          const timeB = b.processingTimeSeconds ?? Number.NEGATIVE_INFINITY;
          return timeB - timeA;
        }
        case 'company-asc':
          return a.companyName.localeCompare(b.companyName);
        case 'company-desc':
          return b.companyName.localeCompare(a.companyName);
        case 'docType-asc':
          return a.documentType.localeCompare(b.documentType);
        case 'docType-desc':
          return b.documentType.localeCompare(a.documentType);
        case 'period-asc':
          return a.documentPeriod.localeCompare(b.documentPeriod);
        case 'period-desc':
          return b.documentPeriod.localeCompare(a.documentPeriod);
        case 'code-asc':
          return a.documentCode.localeCompare(b.documentCode);
        case 'code-desc':
          return b.documentCode.localeCompare(a.documentCode);
        case 'status-asc':
          return a.status.localeCompare(b.status);
        case 'status-desc':
          return b.status.localeCompare(a.status);
        default:
          return 0;
      }
    });
  }, [sortBy]);

  const handleSort = useCallback((column: string) => {
    if (sortBy === `${column}-desc`) {
      setSortBy(`${column}-asc` as SortOption);
    } else {
      setSortBy(`${column}-desc` as SortOption);
    }
  }, [sortBy]);

  const resetSort = useCallback(() => {
    setSortBy('date-desc');
  }, []);

  return {
    sortBy,
    setSortBy,
    sortItems,
    handleSort,
    resetSort,
  };
};





